import React, { useEffect, useMemo, useState } from "react";
import { userService } from "../services/userService";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LuSearch,
  LuUsersRound,
} from "react-icons/lu";
import VendorLayout from "./VendorLayout";
import ActiveIcon from "../icons/ActiveIcon";
import UserAddedSuccessIcon from "../icons/UserAddedSuccessIcon";
import permissionController from "../controllers/permissionController";
import roleController from "../controllers/roleController";
import "./vendorUserManagement.css";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";


const APPLICATION_ID = 3;

const roleFormFeedbackLabels = new Map([
  ["Company id was not found for this user.", "txt_web_vendor_company_id_was_not_found_for_this_user"],
  ["Role was not found.", "txt_web_vendor_role_was_not_found"],
  ["Unable to load role data.", "txt_web_vendor_unable_to_load_role_data"],
  ["Fill role name and description.", "txt_web_vendor_fill_role_name_and_description"],
  ["Unable to save role.", "txt_web_vendor_unable_to_save_role"],
]);
const translateRoleFormFeedback = (message) => {
  const id = roleFormFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? [];

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const mapPermission = (permission) => ({
  id:
    permission.permissionID ||
    permission.permissionId ||
    permission.id ||
    permission.permissionName,
  name: permission.permissionName || permission.name || "Permission",
  hasSourceName: Boolean(permission.permissionName || permission.name),
});

const getRoleId = (role) => role.roleID || role.roleId || role.id;

function VendorRoleForm({ mode = "add" }) {
  useTranslationSnapshot();
  const location = useLocation();
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEdit = mode === "edit";
  const isPreview = location.pathname.includes("-preview");
  const userManagementPath = isPreview
    ? "/vendor-user-management-preview?tab=roles"
    : "/vendor-user-management?tab=roles";
  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchRoleData = async () => {
      setLoading(true);
      setError("");

      try {
        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

        if (!companyId) {
          throw new Error("Company id was not found for this user.");
        }

        const [permissionsResponse, rolesResponse] = await Promise.all([
          permissionController.getAllPermission({
            Authorization,
          }),
          isEdit
            ? roleController.getAllRoles({
                Authorization,
                application: APPLICATION_ID,
                companyId,
              })
            : Promise.resolve(null),
        ]);

        if (
          permissionsResponse?.isAxiosError ||
          permissionsResponse?.response
        ) {
          throw permissionsResponse;
        }

        if (rolesResponse?.isAxiosError || rolesResponse?.response) {
          throw rolesResponse;
        }

        if (!isCurrentRequest) return;

        setPermissions(
          getRows(unwrapPayload(permissionsResponse))
            .map(mapPermission)
            .filter((permission) => permission.id),
        );

        if (isEdit && rolesResponse) {
          const selectedRole = getRows(unwrapPayload(rolesResponse)).find(
            (role) => getRoleId(role) === roleId,
          );

          if (!selectedRole) {
            throw new Error("Role was not found.");
          }

          setRoleName(selectedRole.roleName || selectedRole.name || "");
          setDescription(
            selectedRole.roleDescription || selectedRole.description || "",
          );
          setSelectedPermissions(
            Array.isArray(selectedRole.permissions)
              ? selectedRole.permissions
                  .map((permission) => mapPermission(permission).id)
                  .filter(Boolean)
              : [],
          );
        }
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load role data.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchRoleData();

    return () => {
      isCurrentRequest = false;
    };
  }, [isEdit, roleId]);

  const visiblePermissions = useMemo(() => {
    const query = permissionSearch.trim().toLowerCase();
    return query
      ? permissions.filter((permission) =>
          permission.name.toLowerCase().includes(query),
        )
      : permissions;
  }, [permissionSearch, permissions]);

  const allPermissionsSelected =
    permissions.length > 0 && selectedPermissions.length === permissions.length;

  const togglePermission = (permissionId) => {
    setSelectedPermissions((current) =>
      current.includes(permissionId)
        ? current.filter((item) => item !== permissionId)
        : [...current, permissionId],
    );
  };

  const toggleAllPermissions = () => {
    setSelectedPermissions(
      allPermissionsSelected ? [] : permissions.map((permission) => permission.id),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const safeRoleName = roleName.trim();
    const safeDescription = description.trim();

    if (!safeRoleName || !safeDescription) {
      setError("Fill role name and description.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

      if (!companyId) {
        throw new Error("Company id was not found for this user.");
      }

      const addRole = {
        companyId,
        permissionIDs: selectedPermissions,
        roleDescription: safeDescription,
        roleName: safeRoleName,
      };

      if (isEdit && roleId) {
        addRole.roleId = roleId;
      }

      const response = await roleController.addRole({
        Authorization,
        addRole,
      });

      if (response?.isAxiosError || response?.response) {
        throw response;
      }

      setShowSuccessModal(true);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to save role.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VendorLayout activeKey="users">
      <section className="vendor-role-form-page">
        <Link className="vendor-add-user-back" to={userManagementPath}>
          {translateLabel("txt_web_vendor_back_to_user_management", "Back to User Management")}
        </Link>

        <div className="vendor-role-form-heading">
          <div className="vendor-role-form-title">
            {isEdit ? translateLabel("txt_web_vendor_edit_role_details", "Edit Role Details") : translateLabel("txt_web_vendor_add_role", "Add Role")}
          </div>
          {isEdit && (
            <div className="vendor-role-form-subtitle">
              {translateLabel("txt_web_vendor_update_role_permissions", "Update role information and permissions")}
            </div>
          )}
        </div>

        <form
          className="vendor-role-form-card"
          onSubmit={handleSubmit}
          noValidate
        >
          {error && <div className="vendor-menu-empty-state" role="alert">{translateRoleFormFeedback(error)}</div>}
          {loading && (
            <div className="vendor-menu-empty-state">{translateLabel("txt_web_vendor_loading_role_data", "Loading role data...")}</div>
          )}

          <label className="vendor-role-form-field">
            <span> {translateLabel("txt_web_vendor_role", "Role")}<i>*</i>
            </span>
            <div className="vendor-role-form-select">
              <LuUsersRound aria-hidden="true" />
              <input
                value={roleName}
                aria-label={translateLabel("txt_web_vendor_role", "Role")}
                required
                disabled={loading}
                placeholder={translateLabel("txt_web_vendor_role_name", "Role name")}
                onChange={(event) => setRoleName(event.target.value)}
              />
            </div>
          </label>

          <label className="vendor-role-form-field">
            <span> {translateLabel("input_business_description", "Description")}<i>*</i>
            </span>
            <textarea
              value={description}
              placeholder={translateLabel("txt_web_vendor_write_description", "Write description")}
              aria-label={translateLabel("txt_web_vendor_role_description", "Role Description")}
              required
              disabled={loading}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>

          <label className="vendor-role-permission-search">
            <LuSearch aria-hidden="true" />
            <input
              type="search"
              value={permissionSearch}
              placeholder={translateLabel("txt_web_vendor_search", "Search ...")}
              aria-label={translateLabel("txt_web_vendor_search_permissions", "Search permissions")}
              disabled={loading}
              onChange={(event) => setPermissionSearch(event.target.value)}
            />
          </label>

          <div className="vendor-role-permission-list">
            <label className="vendor-role-permission-header">
              <input
                type="checkbox"
                checked={allPermissionsSelected}
                disabled={loading || permissions.length === 0}
                onChange={toggleAllPermissions}
              />
              <span>{translateLabel("txt_web_vendor_permission", "Permission")}</span>
            </label>

            {!loading &&
              visiblePermissions.map((permission) => (
                <label
                  className="vendor-role-permission-row"
                  key={permission.id}
                >
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(permission.id)}
                    onChange={() => togglePermission(permission.id)}
                  />
                  <span>{permission.hasSourceName ? permission.name : translateLabel("txt_web_vendor_permission", "Permission")}</span>
                </label>
              ))}

            {!loading && visiblePermissions.length === 0 && (
              <div className="vendor-menu-empty-state">
                {translateLabel("txt_web_vendor_no_permissions_found", "No permissions found.")}
              </div>
            )}
          </div>

          <div className="vendor-role-form-actions">
            <button type="submit" disabled={loading || submitting}>
              {submitting
                ? translateLabel("txt_end_user_saving", "Saving...")
                : isEdit
                  ? translateLabel("btn_save_changes", "Save Changes")
                  : translateLabel("txt_web_vendor_create_role", "Create Role")}
            </button>
            <Link to={userManagementPath}>{translateLabel("btn_cancel", "Cancel")}</Link>
          </div>
        </form>

        {showSuccessModal && (
          <div className="vendor-add-user-success-backdrop">
            <div
              className="vendor-add-user-success-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="vendor-role-success-title"
            >
              <div className="vendor-add-user-success-icon">
                <UserAddedSuccessIcon />
                <ActiveIcon />
              </div>
              <div
                className="vendor-add-user-success-title"
                id="vendor-role-success-title"
              >
                {isEdit
                  ? translateLabel("txt_web_vendor_role_updated_successfully", "Role Updated Successfully")
                  : translateLabel("txt_web_vendor_role_added_successfully", "Role Added Successfully")}
              </div>
              <button
                type="button"
                onClick={() => navigate(userManagementPath)}
              > {translateLabel("btn_continue", "Continue")} </button>
            </div>
          </div>
        )}
      </section>
    </VendorLayout>
  );
}

export default VendorRoleForm;
